/**
 * One extrinsic, addressed by its hash (blackbeard/observer#48).
 *
 * The hash is how a wallet names a transaction and how the bridge names a
 * deposit in its NEAR mint, so it is the address a link from anywhere else
 * arrives with. The page is the block page's row for that extrinsic, given room:
 * the call and its arguments, every event it caused, what it paid, and — for a
 * bridge deposit — the mint on NEAR that names it, which is exact because the
 * bridge wrote this hash into the mint.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { TxDetail } from '../api/generated/TxDetail'
import { RequestFailed, fetchTx } from '../api/rest'
import { height as fmtHeight, shortAddress, tokens, utc, when } from '../lib/format'
import { shortNear } from '../lib/near'
import { href } from '../lib/routes'
import { EventList } from './BlockPanel'
import { Field } from './Field'
import { Payload } from './Payload'

export function TxPanel({
  chain,
  hash,
  decimals,
  symbol,
}: {
  chain: string
  hash: string
  decimals: number
  symbol: string
}) {
  const [tx, setTx] = useState<TxDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setTx(null)
    setError(null)
    fetchTx(chain, hash, controller.signal)
      .then(setTx)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain, hash])

  const x = tx?.extrinsic
  const blockRow = tx
    ? `${href({ chain, block: tx.block_hash ?? String(tx.height) })}#x${tx.extrinsic.index}`
    : ''

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Transaction</h2>
        <span className="eyebrow mono-wrap" title={hash}>
          {hash}
        </span>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && !tx && <p className="empty">Reading the transaction…</p>}

      {tx && x && (
        <>
          <div className="node-grid fit">
            <Field
              label="Block"
              value={`#${fmtHeight(tx.height)}, extrinsic ${x.index}`}
              note={x.at ? when(x.at) : undefined}
              title={x.at ? utc(x.at) : undefined}
            />
            <Field label="Call" value={`${x.pallet}::${x.call}`} />
            <Field
              label="Signed by"
              value={x.signer_address ? shortAddress(x.signer_address) : 'inherent'}
              title={x.signer_address ?? undefined}
              note={x.signer_address ? undefined : 'inserted by the block author; nobody signed it'}
            />
            <Field
              label="Result"
              value={x.success === false ? 'failed' : x.success === true ? 'succeeded' : '—'}
            />
            <Field
              label="Fee"
              value={tx.fee ? `${tokens(tx.fee, decimals, 6)} ${symbol}` : '—'}
              note={
                x.signature
                  ? `${x.signature}, ${Math.round(x.signature_bytes / 1024)} KiB signature`
                  : undefined
              }
            />
          </div>

          <p className="panel-note">
            In <Link to={blockRow}>block #{fmtHeight(tx.height)}</Link>
            {x.signer_address && (
              <>
                , signed by{' '}
                <Link to={href({ chain, account: x.signer_address })} title={x.signer_address}>
                  {shortAddress(x.signer_address)}
                </Link>
              </>
            )}
            .
            {tx.bridge_mint && (
              <>
                {' '}
                A bridge deposit: minted on NEAR in{' '}
                <Link to={href({ chain, nearTx: tx.bridge_mint.near_tx })}>
                  {tx.bridge_mint.near_tx.slice(0, 10)}…
                </Link>{' '}
                ({tokens(tx.bridge_mint.amount, decimals)} {symbol}, {when(tx.bridge_mint.at)}) to
                Intents account{' '}
                <Link
                  to={href({ chain, intentsAccount: tx.bridge_mint.account })}
                  title={tx.bridge_mint.account}
                >
                  {shortNear(tx.bridge_mint.account)}
                </Link>
                . The bridge writes this transaction&apos;s hash into the mint, so the link is
                exact.
              </>
            )}
          </p>

          <div className="runtime-group" style={{ padding: '14px 16px 4px' }}>
            <div className="eyebrow">Arguments</div>
            <div style={{ padding: '8px 0' }}>
              <Payload value={x.args} ctx={{ chain, addresses: x.addresses, decimals, symbol }} />
            </div>
          </div>

          <div className="runtime-group" style={{ padding: '14px 16px 12px' }}>
            <div className="eyebrow">Events ({tx.events.length})</div>
            {tx.events.length === 0 ? (
              <p className="panel-note" style={{ border: 0, padding: '8px 0' }}>
                None indexed for this extrinsic.
              </p>
            ) : (
              <EventList
                events={tx.events}
                chain={chain}
                decimals={decimals}
                symbol={symbol}
                wide
              />
            )}
          </div>
        </>
      )}
    </section>
  )
}
